import React, { createContext, useContext, useState, ReactNode } from 'react';

export type UserRol = 'admin' | 'vendedor' | 'cocinero' | 'fabrica' | 'it' | 'contabilidad';

interface LoginPayload {
    token: string;
    username: string;
    localIds?: number[];
    rol?: UserRol;
}

interface AuthContextType {
    user: string | null;
    token: string | null;
    /** Locales a los que pertenece el usuario (relacion muchos-a-muchos). */
    localIds: number[];
    /** Local activo. Arranca en el primero asignado. */
    defaultLocalId: number | null;
    /** Cambia el local activo entre los asignados al usuario. */
    setActiveLocal: (localId: number) => void;
    rol: UserRol | null;
    login: (payload: LoginPayload) => void;
    logout: () => void;
    isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const readStoredLocalIds = (): number[] => {
    const stored = localStorage.getItem('authLocalIds');
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed)) {
                return parsed.map(Number).filter(n => Number.isFinite(n));
            }
        } catch {
            // Valor corrupto: se ignora y se cae al campo legacy de abajo.
        }
    }
    // Sesiones creadas antes del multi-local guardaban un unico id.
    const legacy = localStorage.getItem('authLocalId');
    const parsedLegacy = legacy ? parseInt(legacy, 10) : NaN;
    return Number.isFinite(parsedLegacy) ? [parsedLegacy] : [];
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<string | null>(() => localStorage.getItem('authUser'));
    const [token, setToken] = useState<string | null>(() => localStorage.getItem('authToken'));
    const [localIds, setLocalIds] = useState<number[]>(readStoredLocalIds);
    const [activeLocal, setActiveLocalState] = useState<number | null>(() => {
        const stored = localStorage.getItem('authActiveLocalId');
        const parsed = stored ? parseInt(stored, 10) : NaN;
        if (Number.isFinite(parsed)) return parsed;
        const ids = readStoredLocalIds();
        return ids.length > 0 ? ids[0] : null;
    });
    const [rol, setRol] = useState<UserRol | null>(() => {
        const stored = localStorage.getItem('authRol');
        return (stored as UserRol) || null;
    });

    const login = ({ token: newToken, username, localIds: newLocalIds, rol: newRol }: LoginPayload) => {
        const ids = (newLocalIds || []).filter(n => Number.isFinite(n));
        const firstLocal = ids.length > 0 ? ids[0] : null;
        const rolToStore = newRol || 'admin';

        localStorage.setItem('authToken', newToken);
        localStorage.setItem('authUser', username);
        localStorage.setItem('authLocalIds', JSON.stringify(ids));
        localStorage.setItem('authRol', rolToStore);
        if (firstLocal !== null) {
            localStorage.setItem('authActiveLocalId', String(firstLocal));
        } else {
            localStorage.removeItem('authActiveLocalId');
        }
        // El esquema viejo de un solo local ya no se usa.
        localStorage.removeItem('authLocalId');

        setToken(newToken);
        setUser(username);
        setLocalIds(ids);
        setActiveLocalState(firstLocal);
        setRol(rolToStore);
    };

    const setActiveLocal = (localId: number) => {
        // Solo se puede activar un local asignado al usuario.
        if (!localIds.includes(localId)) return;
        localStorage.setItem('authActiveLocalId', String(localId));
        setActiveLocalState(localId);
    };

    const logout = () => {
        ['authToken', 'authUser', 'authLocalIds', 'authActiveLocalId', 'authLocalId', 'authRol']
            .forEach(key => localStorage.removeItem(key));
        setToken(null);
        setUser(null);
        setLocalIds([]);
        setActiveLocalState(null);
        setRol(null);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                token,
                localIds,
                defaultLocalId: activeLocal,
                setActiveLocal,
                rol,
                login,
                logout,
                isAuthenticated: !!token,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};
